import { useEffect, useState, type ReactElement } from 'react';
import { LIVE_EMBER_EVENT } from '../live';
import { apiErrorCode, emberApi, isAuthRequired, type Knock, type MeEmberItem, type MeEmberState } from '../api';
import { refreshChats } from '../chats-store';
import { trackFailed } from '../../../../../shared-web/track';
import { navigate } from '../router';
import { useTalkSheet } from '../talk-sheet';
import { MyEmberCard } from '../components/MyEmberCard';
import { isLetter, isQuestion, isRoomInvite, letterId } from '../components/knock-letters';
import { KnockLetter } from '../components/KnockLetter';
import { useKnocks } from '../components/use-knocks';
import { Modal } from '../components/Modal';
import { ActionArrow } from '../components/ActionArrow';
import { PageLoading } from '../components/PageLoading';
import { SectionEmpty } from '../components/SectionEmpty';

/**
 * S12 — My Embers (§4.12): every telling this person has made, at whatever
 * stage it is in — being written, written but unsaved, published with its
 * private impact, ended too short to write from, or ended without an Ember.
 * Popularity never appears; resonance is owner-only.
 *
 * In-flight cards and the empty list right after hanging up are read again
 * when the live channel says an Ember changed (`ember`, live.ts) and on each
 * return to the tab — until 2026-10-21 they were polled every 5 s (and an
 * empty list re-asked six times: a voice telling has no row until the
 * post-call webhook lands).
 */

/**
 * The cards a person can throw away: a telling that produced no Ember and is
 * not being written right now.
 *
 * Nothing came out of these, so nothing else points at them and deleting one
 * costs no one anything — and for `stalled` and `failed` this list is the only
 * door that reaches the words at all. `writing` and `slow` are left out on
 * purpose: a finalize job is holding those, the server refuses them
 * (`conversation_busy`), and a control that cannot keep its promise is worse
 * than no control. A telling that DID become an Ember is dealt with from the
 * Ember, never from a row in a list.
 */
const DELETABLE_STATES: readonly MeEmberState[] = ['below_floor', 'needs_more_context', 'stalled', 'failed'];

/**
 * Why a delete did not happen, in the person's terms. Both refusals describe
 * the row changing under them between load and tap, so each says what the row
 * became rather than asking them to try the same thing again.
 */
function deleteErrorText(code: string): string {
  if (code === 'conversation_busy') return 'This one started being written just now. Give it a minute and try again.';
  if (code === 'conversation_has_output') return 'This one became an Ember while you were here — open it to delete it from there.';
  return "That didn't go through. Try again.";
}

/**
 * What the red dot points at comes first (2026-09-20 拍板): the rows carrying a
 * change — a letter waiting, a note not yet read — move to the top of the list;
 * the rest keep the order they had. A stable partition, never a re-sort.
 */
function toTop<T>(rows: T[], changed: (row: T) => boolean): T[] {
  return [...rows.filter(changed), ...rows.filter((r) => !changed(r))];
}

export function MyEmbersPage(): ReactElement {
  const openTalk = useTalkSheet();
  const [items, setItems] = useState<MeEmberItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  /** The card whose Delete was tapped, waiting on confirmation. */
  const [pendingDelete, setPendingDelete] = useState<MeEmberItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  /**
   * One line standing where a deleted telling was. A delete leaves a person
   * holding nothing, and the next thing they might want is the thing this page
   * exists for — so the empty space says so instead of the row silently
   * vanishing. It lives only until the next load.
   */
  const [deletedCount, setDeletedCount] = useState(0);
  /**
   * The letters (§4.9): every letter about one of these Embers, each a door line
   * on the card of the Ember it is about, with the red dot the Your Embers
   * tab counts (2026-09-05 walkthrough: the ask showed only in the chat thread
   * and on the Ember's own page; 2026-09-06: all three letter states, in the
   * letter's own sentence; 2026-09-08: one line per letter, the dot, and a tap
   * opens the paper right here — the tab's number, the dots, and the paper are
   * one fact). A failed read leaves the map empty: the cards are complete
   * without it.
   */
  const { knocks, update: updateKnock } = useKnocks();
  const letters = new Map<string, Knock[]>();
  // Question letters (§4.19) and room invitations (§4.21) are about no
  // Ember: a room's lives in the Chats list (2026-10-20), and Ask people
  // has stopped. A read of someone else's Ember (read_reader) belongs to none of
  // these cards either. Here: the share ask and a reader's note, each under
  // the owner's own Ember.
  for (const k of knocks.filter(isLetter)) {
    if (isQuestion(k) || isRoomInvite(k) || k.role === 'read_reader') continue;
    const id = k.ember.id;
    if (!id) continue;
    const list = letters.get(id) || [];
    list.push(k);
    letters.set(id, list);
  }
  /** The letter picked up from a card (letterId), on top of the paper's pile. */
  const [openLetter, setOpenLetter] = useState('');

  // Since 2026-10-21 (the live channel) no clock: an Ember written, or its picture or music landed, arrives as the
  // person's `ember` event and the list is read again then, and on each return to the tab (an Ember in flight, the
  // empty list right after a call — the web polled every 5 s for those until then)
  useEffect(() => {
    let alive = true;

    const load = (): void => {
      emberApi.myEmbers()
        .then((r) => {
          if (!alive) return;
          setItems(r.items);
        })
        .catch((err: unknown) => {
          if (!alive) return;
          if (isAuthRequired(err)) navigate('/auth?returnTo=%2Fme%2Fembers', { replace: true });
          else {
            setFailed(true);
            trackFailed('my_embers_load', 'load_failed');
          }
        });
    };

    load();
    const onLive = (): void => load();
    const onVisible = (): void => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener(LIVE_EMBER_EVENT, onLive);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.removeEventListener(LIVE_EMBER_EVENT, onLive);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const confirmDelete = (): void => {
    const target = pendingDelete;
    const conversationId = target?.conversation_id;
    if (!target || !conversationId || deleting) return;
    setDeleting(true);
    setDeleteError('');
    emberApi.deleteConversation(conversationId)
      .then(() => {
        // Gone on the server, so gone here — no refetch to wait through.
        setItems((current) => (current || []).filter((i) => i.key !== target.key));
        setDeletedCount((n) => n + 1);
        setPendingDelete(null);
        // The Chats list's Ember row may be this conversation (2026-10-31): read it again.
        void refreshChats();
      })
      .catch((err: unknown) => {
        if (isAuthRequired(err)) navigate('/auth?returnTo=%2Fme%2Fembers', { replace: true });
        else {
          const code = apiErrorCode(err);
          setDeleteError(deleteErrorText(code));
          trackFailed(
            'ember_delete',
            code === 'conversation_busy' || code === 'conversation_has_output' ? code : 'error',
            { conversation_id: conversationId },
          );
        }
      })
      .finally(() => setDeleting(false));
  };

  if (failed) return <p className="ember-page-quiet">Couldn't load your Embers. Refresh to try again.</p>;
  if (items === null) return <PageLoading />;

  const tellAnother = (
    <button
      type="button"
      className="me-tell-another ember-text-action"
      data-log="talk_open"
      data-log-discovery="my_embers_post_delete"
      onClick={() => openTalk({ entryType: 'home', returnTo: '/me/embers' })}
    >
      <span>Tell another experience</span>
      <ActionArrow />
    </button>
  );

  const deletePrompt = pendingDelete && (
    <Modal
      onClose={() => { if (!deleting) { setPendingDelete(null); setDeleteError(''); } }}
      labelledBy="delete-telling-title"
      dismissible={!deleting}
      // Throwing a telling away is answered where the thumb already is on a
      // phone, and as the ordinary centred card on a desktop.
      sheet="phone"
      className="me-confirm"
    >
      <h2 id="delete-telling-title" className="me-confirm-title">Delete this?</h2>
      <p className="me-confirm-text">
        This removes what you said and any photos you sent with it. It cannot be
        undone.
      </p>
      {deleteError && <p className="me-delete-error">{deleteError}</p>}
      <div className="me-confirm-actions">
        <button
          type="button"
          className="ember-btn ember-btn-ghost"
          data-log="my_ember_delete_cancel"
          onClick={() => { setPendingDelete(null); setDeleteError(''); }}
          disabled={deleting}
        >
          Keep it
        </button>
        <button
          type="button"
          className="ember-btn ember-btn-primary"
          data-log="my_ember_delete_commit"
          data-log-conversation-id={pendingDelete.conversation_id || undefined}
          data-log-ember-id={pendingDelete.ember_id || undefined}
          data-log-state={pendingDelete.state}
          onClick={confirmDelete}
          disabled={deleting}
          data-autofocus
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </Modal>
  );

  if (items.length === 0) {
    return (
      <>
        <div
          className="me-empty"
          data-log-seen="my_embers_empty"
          data-log-state={deletedCount > 0 ? 'deleted' : 'default'}
        >
          <SectionEmpty
            icon="ember"
            action={(
              <button
                type="button"
                data-log="talk_open"
                data-log-discovery="my_embers_empty"
                onClick={() => openTalk({ entryType: 'home', returnTo: '/me/embers' })}
                className="ember-btn ember-btn-primary"
              >
                Talk about a real experience
              </button>
            )}
          >
            {deletedCount > 0
              ? 'Deleted. Tell one real experience — it stays yours.'
              : 'No Embers yet. Tell one real experience — it stays yours.'}
          </SectionEmpty>
        </div>
        {deletePrompt}
      </>
    );
  }

  return (
    <>
      {/* The notebook's title line (§4.12, 2026-09-04 second pass): one quiet
          sentence over the pages saying what this place is — not a feed, not
          an archive to administer. */}
      <p className="my-embers-epigraph">What you’ve said and kept here.</p>
      <div className="me-list my-embers-list">
        {toTop(items, (item) => Boolean(item.ember_id && letters.get(item.ember_id)?.some(isLetter))).map((item) => (
          <div key={item.key} className="me-list-item">
            <MyEmberCard
              item={item}
              letters={item.ember_id ? letters.get(item.ember_id) : undefined}
              onOpenLetter={(k) => setOpenLetter(letterId(k))}
              onDelete={item.conversation_id && DELETABLE_STATES.includes(item.state)
                ? () => { setDeleteError(''); setPendingDelete(item); }
                : undefined}
            />
          </div>
        ))}
        {deletedCount > 0 && (
          <div className="me-list-item me-deleted-note">
            <p>{deletedCount === 1 ? 'Deleted.' : `${deletedCount} deleted.`}</p>
            {tellAnother}
          </div>
        )}
      </div>
      {deletePrompt}
      {openLetter ? (
        <KnockLetter
          knocks={knocks}
          onChange={updateKnock}
          initialExpanded={openLetter}
          onClose={() => setOpenLetter('')}
        />
      ) : null}
    </>
  );
}
